import "./InsuranceProfileReviewInfo.scss";

import { ReactNode, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import Text from "components/Text/Text";
import { ClassName, DynamicFormItems, TextFormItem } from "config";
import { DynamicFormItemComponent } from "containers/DynamicForm/DynamicFormItems";
import { useTranslation } from "modules";
import { useAppDispatch } from "store";
import { getProfileReviewPersonalInfo } from "store/actions/profile";
import { selectInsuranceProfileReviewInfo } from "store/selectors/selectInsuranceProfileReviewInfo";

import classNames from "classnames";

interface IBaseInsuranceProfileReviewInfo {
  children?: ReactNode;
}

interface IInsuranceProfileReviewInfo {
  className?: ClassName;
  dynamicFormItems?: DynamicFormItems[];
}

interface InsuranceProfileReviewInfoItem {
  label: string;
  value: string;
}

export function InsuranceProfileReviewInfo({
  className,
  dynamicFormItems = [],
}: IInsuranceProfileReviewInfo) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const profileReviewInfo = useSelector(selectInsuranceProfileReviewInfo);

  const dynamicHeader = dynamicFormItems?.find(item => item.key === "personal-info-header");
  const taxIdItem = dynamicFormItems?.find<TextFormItem>(
    (item): item is TextFormItem => item.key === "tax-id-number-value"
  );
  const taxpayerIdNumber = useMemo(() => {
    // For Non-NY Profile Review, we are no longer showing the SSN
    // For NY Profile Review, we still need to show a masked SSN value.
    if (!taxIdItem) {
      return undefined;
    }
    return profileReviewInfo.ssn;
  }, [profileReviewInfo.ssn, taxIdItem]);

  const insuranceReviewInfoItems = dynamicFormItems.filter(item => {
    const keys = [
      "terminal-illness-rider-section",
      "spacer-to-terminal-illness-section",
      "spacer-divider-to-acknowledgements",
    ];
    return keys.some(key => item.key === key);
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(getProfileReviewPersonalInfo()).unwrap();
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, [dispatch]);

  return (
    <div className={classNames("insurance-profile-review-info", className)}>
      {!dynamicHeader && (
        <InsuranceProfileReviewInfoHeader>
          {t("uip.review.ppi.header", "Personal info")}
        </InsuranceProfileReviewInfoHeader>
      )}

      {dynamicHeader && (
        <>
          <DynamicFormItemComponent
            item={dynamicHeader}
            className="personal-info-header__wrapper"
          />
        </>
      )}

      <InsuranceProfileReviewInfoItem
        label={t("uip.review.ppi.name.label", "Name")}
        value={profileReviewInfo.name}
      />
      <InsuranceProfileReviewInfoItem
        label={t("uip.review.ppi.birthdate.label", "Birthdate")}
        value={profileReviewInfo.birthDate}
      />
      <InsuranceProfileReviewInfoItem
        label={t("uip.review.ppi.phone.label", "Phone number")}
        value={profileReviewInfo.phoneNumber}
      />
      <InsuranceProfileReviewInfoItem
        label={t("uip.review.ppi.gender.label", "Gender")}
        value={profileReviewInfo.gender}
      />
      <InsuranceProfileReviewInfoItem
        label={t("uip.review.ppi.address.label", "Primary Residence")}
        value={profileReviewInfo.address}
      />
      {taxpayerIdNumber && (
        <InsuranceProfileReviewInfoItem
          label={t("uip.review.ppi.ssn.label", "Taxpayer ID Number")}
          value={taxpayerIdNumber}
        />
      )}

      {insuranceReviewInfoItems.map(item => (
        <DynamicFormItemComponent item={item} key={item.key} />
      ))}
    </div>
  );
}

function InsuranceProfileReviewInfoHeader({ children }: IBaseInsuranceProfileReviewInfo) {
  return (
    <Text className="insurance-profile-review-info__header" tag="l3">
      {children}
    </Text>
  );
}

function InsuranceProfileReviewInfoItem({ label, value }: InsuranceProfileReviewInfoItem) {
  return (
    <div
      className="insurance-profile-review-info__item insurance-profile-review-info-item"
      key={label}>
      <Text className="insurance-profile-review-info-item__label" tag="p5">
        {label}
      </Text>
      <Text className="insurance-profile-review-info-item__value" tag="l4">
        {value}
      </Text>
    </div>
  );
}

export function InsuranceProfileReviewInfoMessage({ children }: IBaseInsuranceProfileReviewInfo) {
  return (
    <Text className="insurance-profile-review-info__message" tag="p6">
      {children}
    </Text>
  );
}
